import { extendTheme } from '@chakra-ui/react';

/** Creator revamp palette: lime accent, Archivo 600 sentence-case headings. Use `ff.*` tokens in new and revamped screens. */
export const ff = {
  ink: '#0E0E0E',
  lime: '#C6F432',
  /** Solid lime; kept as a gradient token so `bgImage` call sites stay valid. */
  limeGradient: 'linear-gradient(#C6F432, #C6F432)',
  /** Accent text for links, checks and ticks on white (lime is unreadable on white). */
  accentText: '#0E0E0E',
  accent: '#0E0E0E',
  body: '#3E3E42',
  card: '#E4E4E0',
  text2: '#66666A',
  muted: '#9A9A9E',
  faint: '#B4B4B8',
  border: '#DCDCD8',
  line: '#ECECEA',
  limeSurface: '#F1FADF',
  cream: '#F7F2E8',
  grey: '#F2F2F0',
  inkSoft: '#1F1F21',
  danger: '#D93025',
};

/** Floating nav: 60px pill parked 24px above the bottom edge. */
export const navClearance = 'calc(108px + env(safe-area-inset-bottom))';
export const navCtaOffset = 'calc(96px + env(safe-area-inset-bottom))';

const display = `'Archivo', system-ui, sans-serif`;

const theme = extendTheme({
  fonts: {
    heading: `'Inter', sans-serif`,
    body: `'Inter', sans-serif`,
    display,
  },
  colors: {
    ff,
  },
  radii: {
    card: '20px',
    button: '18px',
  },
  textStyles: {
    display: {
      fontFamily: display,
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.12,
    },
    eyebrow: {
      fontFamily: display,
      fontSize: '11px',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  components: {
    Button: {
      variants: {
        lime: {
          bg: ff.lime,
          bgImage: ff.limeGradient,
          color: ff.ink,
          borderRadius: 'button',
          fontFamily: display,
          fontWeight: 600,
          h: '56px',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)',
          _hover: { bgImage: 'none', bg: '#B8E62A', _disabled: { bg: ff.lime } },
          _active: { bgImage: 'none', bg: '#AED923' },
        },
        ink: {
          bg: ff.ink,
          color: 'white',
          borderRadius: 'button',
          fontFamily: display,
          fontWeight: 600,
          h: '56px',
          _hover: { bg: '#2A2A2C', _disabled: { bg: ff.ink } },
        },
        inkOutline: {
          bg: 'white',
          color: ff.ink,
          border: '1.5px solid',
          borderColor: ff.ink,
          borderRadius: 'button',
          fontFamily: display,
          fontWeight: 600,
          h: '56px',
          _hover: { bg: ff.grey },
        },
        danger: {
          bg: ff.danger,
          color: 'white',
          borderRadius: 'button',
          fontFamily: display,
          fontWeight: 600,
          h: '56px',
          _hover: { bg: '#B9271E' },
        },
      },
    },
    // Form fields: outline look shared by every creator form (explicit props still win).
    Input: {
      variants: {
        outline: {
          field: {
            h: '48px',
            borderRadius: '14px',
            borderColor: ff.border,
            bg: 'white',
            _hover: { borderColor: ff.muted },
            _focusVisible: { borderColor: ff.ink, boxShadow: `0 0 0 1px ${ff.ink}` },
            _invalid: { borderColor: ff.danger, boxShadow: `0 0 0 1px ${ff.danger}` },
          },
        },
      },
    },
    Textarea: {
      variants: {
        outline: {
          borderRadius: '14px',
          borderColor: ff.border,
          bg: 'white',
          _hover: { borderColor: ff.muted },
          _focusVisible: { borderColor: ff.ink, boxShadow: `0 0 0 1px ${ff.ink}` },
          _invalid: { borderColor: ff.danger, boxShadow: `0 0 0 1px ${ff.danger}` },
        },
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            h: '48px',
            borderRadius: '14px',
            borderColor: ff.border,
            bg: 'white',
            _hover: { borderColor: ff.muted },
            _focusVisible: { borderColor: ff.ink, boxShadow: `0 0 0 1px ${ff.ink}` },
          },
        },
      },
    },
    FormLabel: {
      baseStyle: { fontSize: '14px', fontWeight: 600, color: ff.ink, mb: '6px' },
    },
    Switch: {
      baseStyle: { track: { _checked: { bg: ff.ink } }, thumb: { _checked: { bg: ff.lime } } },
    },
    Checkbox: {
      baseStyle: {
        control: { borderColor: ff.ink, _checked: { bg: ff.ink, borderColor: ff.ink, color: ff.lime, _hover: { bg: ff.ink, borderColor: ff.ink } } },
      },
    },
  },
});

export default theme;
